"use client";

import { useRouter } from "next/navigation";

import { KeyboardEvent, useEffect, useMemo, useRef, useState } from "react";

import { useUser } from "@clerk/nextjs";
import { format, isToday, isWithinInterval, subDays } from "date-fns";
import { Search, Trash2, X } from "lucide-react";
import { toast } from "sonner";

import { RelativeTime } from "@/frontend/components/shared/relative-time";
import { Button } from "@/frontend/components/ui/button";
import { CommandShortcut } from "@/frontend/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/frontend/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/frontend/components/ui/drawer";
import { Input } from "@/frontend/components/ui/input";
import { Label } from "@/frontend/components/ui/label";
import { useDeleteJam, useJamsQuery } from "@/frontend/hooks/jam";
import { useIsMobile } from "@/frontend/hooks/utils";
import { getErrorMessage } from "@/frontend/lib/error-utils";
import { useSidebarStore } from "@/frontend/lib/stores";
import { cn } from "@/frontend/lib/utils";

type ItemType = "jam";

interface SelectableItem {
  id: string;
  type: ItemType;
  title: string;
  status?: string;
  updatedAt?: string;
  handleAction: () => void;
}

interface ActionItem {
  id: string;
  smTitle: string;
  title: string;
  color: string;
  handleAction: () => void;
}

export function SearchDialog() {
  const { user } = useUser();
  const router = useRouter();
  const {
    data: jamsData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useJamsQuery(50);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [isMounted, setIsMounted] = useState(false);
  const isMobile = useIsMobile();
  const [isKeyboardMode, setIsKeyboardMode] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const dialogContentRef = useRef<HTMLDivElement>(null);
  const itemsContainerRef = useRef<HTMLDivElement>(null);
  const { isSearchDialogOpen, setIsSearchDialogOpen } = useSidebarStore();

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const allJams = useMemo(
    () => jamsData?.pages.flatMap((page) => page.jams) ?? [],
    [jamsData?.pages],
  );
  const today = new Date();
  const startOfLastWeek = subDays(today, 7);

  useEffect(() => {
    setConfirmDeleteId(null);
  }, [selectedItemId]);

  useEffect(() => {
    if (!isSearchDialogOpen) {
      setSearchTerm("");
      setConfirmDeleteId(null);
      setIsKeyboardMode(false);
    }
  }, [isSearchDialogOpen]);

  const deleteJamMutation = useDeleteJam();

  const actions: ActionItem[] = [
    {
      id: "create-jam",
      smTitle: "New jam",
      title: "Create jam",
      color: "bg-sky-400",
      handleAction: () => {
        router.push("/");
        setIsSearchDialogOpen(false);
      },
    },
    {
      id: "browse-apps",
      smTitle: "Apps",
      title: "Browse apps",
      color: "bg-emerald-400",
      handleAction: () => {
        router.push("/apps");
        setIsSearchDialogOpen(false);
      },
    },
    {
      id: "view-projects",
      smTitle: "Projects",
      title: "View projects",
      color: "bg-violet-400",
      handleAction: () => {
        router.push("/projects");
        setIsSearchDialogOpen(false);
      },
    },
  ];

  const jamItems: SelectableItem[] = allJams.map((jam) => ({
    id: jam.id,
    type: "jam" as const,
    title: jam.title || "Untitled Jam",
    status: jam.status,
    updatedAt: jam.updatedAt.toString(),
    handleAction: () => handleGoToJam(jam.id),
  }));

  // Don't show actions when searching
  const showActions = !searchTerm;

  const filteredJamItems = searchTerm
    ? jamItems.filter((item) =>
        item.title.toLowerCase().includes(searchTerm.toLowerCase()),
      )
    : jamItems;

  const todayJams = filteredJamItems.filter(
    (item) => item.updatedAt && isToday(new Date(item.updatedAt)),
  );

  const lastWeekJams = filteredJamItems.filter((item) => {
    if (!item.updatedAt) return false;
    const updatedAt = new Date(item.updatedAt);
    return (
      !isToday(updatedAt) &&
      isWithinInterval(updatedAt, { start: startOfLastWeek, end: today })
    );
  });

  const earlierJams = filteredJamItems.filter((item) => {
    if (!item.updatedAt) return false;
    const updatedAt = new Date(item.updatedAt);
    return (
      !isToday(updatedAt) &&
      !isWithinInterval(updatedAt, { start: startOfLastWeek, end: today })
    );
  });

  const allItems = useMemo(
    () => [...todayJams, ...lastWeekJams, ...earlierJams],
    [todayJams, lastWeekJams, earlierJams],
  );

  const getSelectedItem = (): SelectableItem | undefined => {
    return allItems.find((item) => item.id === selectedItemId);
  };

  useEffect(() => {
    if (
      allItems.length > 0 &&
      (!selectedItemId || !allItems.some((item) => item.id === selectedItemId))
    ) {
      setSelectedItemId(allItems[0].id);
    } else if (allItems.length === 0) {
      setSelectedItemId(null);
    }
  }, [allItems, selectedItemId]);

  // Reset keyboard mode when mouse moves
  useEffect(() => {
    const handleMouseMove = () => {
      if (isKeyboardMode) {
        setIsKeyboardMode(false);
      }
    };

    if (isSearchDialogOpen && isKeyboardMode) {
      window.addEventListener("mousemove", handleMouseMove);
      return () => window.removeEventListener("mousemove", handleMouseMove);
    }
  }, [isSearchDialogOpen, isKeyboardMode]);

  if (!user || !isMounted) return null;

  const handleGoToJam = (jamId: string | null) => {
    if (!jamId) return;
    router.push(`/jam/${jamId}`);
    setIsSearchDialogOpen(false);
  };

  const scrollItemIntoView = (itemId: string) => {
    if (!itemsContainerRef.current) return;

    const element = document.getElementById(`item-${itemId}`);
    if (element) {
      const container = itemsContainerRef.current;
      const containerRect = container.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();

      const buffer = 24;
      const isInView =
        elementRect.top >= containerRect.top + buffer &&
        elementRect.bottom <= containerRect.bottom - buffer;

      if (!isInView) {
        element.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (allItems.length === 0) return;

    // Enable keyboard mode when using keyboard navigation
    if (["ArrowDown", "ArrowUp", "Enter"].includes(e.key)) {
      setIsKeyboardMode(true);
    }

    if (confirmDeleteId) {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        setConfirmDeleteId(null);
        return;
      }
      if (e.key === "Enter") {
        e.preventDefault();
        deleteJamMutation.mutate(
          { id: confirmDeleteId },
          {
            onSuccess: () => {
              toast.success("Jam deleted successfully");
            },
            onError: (error) => {
              toast.error(getErrorMessage(error, "Failed to delete jam"));
            },
          },
        );
        setConfirmDeleteId(null);
        return;
      }
      return;
    }

    const currentIndex = allItems.findIndex(
      (item) => item.id === selectedItemId,
    );

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (currentIndex < allItems.length - 1) {
          const nextItemId = allItems[currentIndex + 1].id;
          setSelectedItemId(nextItemId);
          scrollItemIntoView(nextItemId);
        } else {
          const firstItemId = allItems[0].id;
          setSelectedItemId(firstItemId);
          scrollItemIntoView(firstItemId);
        }
        break;
      case "ArrowUp":
        e.preventDefault();
        if (currentIndex > 0) {
          const prevItemId = allItems[currentIndex - 1].id;
          setSelectedItemId(prevItemId);
          scrollItemIntoView(prevItemId);
        } else {
          const lastItemId = allItems[allItems.length - 1].id;
          setSelectedItemId(lastItemId);
          scrollItemIntoView(lastItemId);
        }
        break;
      case "Enter":
        e.preventDefault();
        const selectedItem = getSelectedItem();
        if (selectedItem) {
          selectedItem.handleAction();
        }
        break;
      case "d":
      case "D":
        if ((e.metaKey || e.ctrlKey) && e.shiftKey) {
          e.preventDefault();
          const selectedItem = getSelectedItem();
          if (selectedItem) {
            handleDeleteClick(selectedItem.id);
          }
        }
        break;
    }
  };

  const handleDeleteClick = (itemId: string) => {
    if (confirmDeleteId === itemId) {
      deleteJamMutation.mutate(
        { id: itemId },
        {
          onSuccess: () => {
            toast.success("Jam deleted successfully");
          },
          onError: (error) => {
            toast.error(getErrorMessage(error, "Failed to delete jam"));
          },
        },
      );
      setConfirmDeleteId(null);
    } else {
      setConfirmDeleteId(itemId);
    }
  };

  const cancelDelete = () => {
    setConfirmDeleteId(null);
  };

  const renderItem = (item: SelectableItem) => {
    const isConfirmingDelete = confirmDeleteId === item.id;
    const isJam = item.type === "jam";
    const isSelected = selectedItemId === item.id;

    return (
      <div
        id={`item-${item.id}`}
        key={item.id}
        className="group relative flex items-center gap-2"
      >
        <div
          className={cn(
            "relative flex h-auto w-full flex-1 cursor-pointer items-center justify-between overflow-hidden rounded-lg p-3 text-muted transition-none select-none",
            isSelected && "bg-muted-foreground/20 text-white",
            isMobile && "pr-12",
            isKeyboardMode && "pointer-events-none",
          )}
          onMouseEnter={() => {
            if (!confirmDeleteId && !isKeyboardMode) {
              setSelectedItemId(item.id);
            }
          }}
          onClick={(e) => {
            if (confirmDeleteId) {
              e.preventDefault();
              return;
            }
            item.handleAction();
          }}
        >
          <span className="flex flex-grow items-center gap-2 overflow-hidden pr-2 text-left md:pr-4">
            <p className="truncate text-sm">{item.title}</p>
            {item.status && item.status !== "idle" && (
              <div className="size-2 flex-shrink-0 animate-pulse rounded-full bg-primary" />
            )}
          </span>
          {item.updatedAt && (
            <span
              className={cn(
                "hidden flex-shrink-0 text-xs text-muted-foreground md:block",
                !isMobile && isJam && isSelected && "group-hover:hidden",
              )}
            >
              {item.updatedAt && isToday(new Date(item.updatedAt)) ? (
                <RelativeTime date={new Date(item.updatedAt)} />
              ) : (
                format(new Date(item.updatedAt), "MMM d, h:mm a")
              )}
            </span>
          )}

          {/* Desktop hover delete button */}
          {!isMobile && isJam && isSelected && (
            <div className="absolute right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100">
              {isConfirmingDelete ? (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-destructive hover:bg-destructive/20"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteClick(item.id);
                    }}
                  >
                    <Trash2 size={16} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 hover:bg-muted-foreground/30"
                    onClick={(e) => {
                      e.stopPropagation();
                      cancelDelete();
                    }}
                  >
                    <X size={16} />
                  </Button>
                </>
              ) : (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground hover:bg-destructive/20 hover:text-destructive"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteClick(item.id);
                  }}
                >
                  <Trash2 size={16} />
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Mobile delete button */}
        {isMobile && isJam && (
          <div className="flex items-center">
            {isConfirmingDelete ? (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-destructive"
                  onClick={() => handleDeleteClick(item.id)}
                >
                  <Trash2 />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={cancelDelete}
                  className="text-white"
                >
                  <X />
                </Button>
              </>
            ) : (
              <Button
                variant="ghost"
                size="icon"
                className="text-muted-foreground hover:text-destructive"
                onClick={() => handleDeleteClick(item.id)}
              >
                <Trash2 />
              </Button>
            )}
          </div>
        )}
      </div>
    );
  };

  const SearchHeader = (
    <div className="flex h-16 items-center justify-between border-b border-muted-foreground/10 pr-4 pl-6">
      <div className="flex items-center">
        <Search size={20} className="shrink-0 text-muted-foreground" />
        <Input
          ref={searchInputRef}
          autoFocus={!isMobile}
          placeholder="Search anything..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="bg-transparent text-accent dark:bg-transparent"
        />
      </div>
      <CommandShortcut
        onClick={() => setIsSearchDialogOpen(false)}
        className="cursor-pointer"
      >
        Esc
      </CommandShortcut>
    </div>
  );

  const handleScroll = () => {
    if (!itemsContainerRef.current || isFetchingNextPage || !hasNextPage)
      return;

    const { scrollTop, scrollHeight, clientHeight } = itemsContainerRef.current;
    if (scrollTop + clientHeight >= scrollHeight - 100) {
      fetchNextPage();
    }
  };

  const MainContent = (
    <div
      ref={itemsContainerRef}
      className="overflow-y-auto py-4"
      onScroll={handleScroll}
      style={{
        height: isMobile ? "auto" : "calc(100vh-16rem)",
        maxHeight: isMobile ? "none" : "640px",
      }}
    >
      <div className="flex flex-col gap-8 px-4">
        {showActions && (
          <div className="grid grid-cols-3 gap-4">
            {actions.map((action) => (
              <div
                key={action.id}
                onClick={action.handleAction}
                className={cn(
                  "flex h-24 cursor-pointer flex-col justify-end rounded-xl p-4 text-white transition-transform hover:scale-[1.02]",
                  action.color,
                )}
              >
                <span className="text-sm font-medium md:hidden">
                  {action.smTitle}
                </span>
                <span className="hidden text-sm font-medium md:block">
                  {action.title}
                </span>
              </div>
            ))}
          </div>
        )}

        {todayJams.length > 0 && (
          <div className="flex flex-col">
            <Label className="mb-2 pl-3 text-muted">Today</Label>
            {todayJams.map(renderItem)}
          </div>
        )}

        {lastWeekJams.length > 0 && (
          <div className="flex flex-col">
            <Label className="mb-2 pl-3 text-muted">Last week</Label>
            {lastWeekJams.map(renderItem)}
          </div>
        )}

        {earlierJams.length > 0 && (
          <div className="flex flex-col">
            <Label className="mb-2 pl-3 text-muted">Earlier</Label>
            {earlierJams.map(renderItem)}
          </div>
        )}

        {allJams.length === 0 && (
          <p className="py-4 text-center text-sm text-muted-foreground">
            No recent jams found. Start a new one!
          </p>
        )}
        {allJams.length > 0 &&
          searchTerm &&
          todayJams.length === 0 &&
          lastWeekJams.length === 0 &&
          earlierJams.length === 0 && (
            <p className="py-4 text-center text-sm text-muted-foreground">
              No results found for &ldquo;{searchTerm}&rdquo;
            </p>
          )}
      </div>
    </div>
  );

  const ActionFooter = (
    <div className="flex h-12 items-center justify-center border-t border-muted-foreground/10 pr-2 pl-4">
      {confirmDeleteId && getSelectedItem()?.id === confirmDeleteId ? (
        <>
          <div className="flex-1 pl-2 text-xs text-muted-foreground">
            Delete &ldquo;
            {getSelectedItem()?.title}&rdquo;?
          </div>
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={cancelDelete}
              className="pr-1 pl-2 text-xs text-muted hover:bg-muted-foreground/20 hover:text-white"
            >
              Cancel <CommandShortcut>Esc</CommandShortcut>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleDeleteClick(confirmDeleteId)}
              className="group rounded-br-xl pr-1 pl-2 text-xs text-destructive hover:bg-destructive/20 hover:text-destructive"
            >
              Confirm delete
              <CommandShortcut className="group-hover:rounded-br-lg">
                ↵
              </CommandShortcut>
            </Button>
          </div>
        </>
      ) : (
        <>
          <div className="hidden flex-1 sm:block"></div>
          <div className="flex gap-1">
            {selectedItemId && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  className="pr-1 pl-2 text-xs text-muted hover:bg-muted-foreground/20 hover:text-white"
                  onClick={() => {
                    const selectedItem = getSelectedItem();
                    if (selectedItem) {
                      selectedItem.handleAction();
                    }
                  }}
                  disabled={!selectedItemId}
                >
                  Go <CommandShortcut>↵</CommandShortcut>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="group rounded-br-xl pr-1 pl-2 text-xs text-muted hover:bg-muted-foreground/20 hover:text-white"
                  disabled={!selectedItemId}
                  onClick={() => {
                    const selectedItem = getSelectedItem();
                    if (selectedItem) {
                      handleDeleteClick(selectedItem.id);
                    }
                  }}
                >
                  Delete{" "}
                  <CommandShortcut className="group-hover:rounded-br-lg">
                    ⇧ ⌘ D
                  </CommandShortcut>
                </Button>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );

  if (isMobile) {
    return (
      <Drawer
        open={isSearchDialogOpen}
        onOpenChange={(newOpen) => {
          // Prevent closing during delete confirmation
          if (confirmDeleteId && !newOpen) {
            return;
          }
          setIsSearchDialogOpen(newOpen);
        }}
      >
        <DrawerContent className="flex h-full overflow-hidden p-0">
          <div
            className="flex h-full flex-col"
            onKeyDown={handleKeyDown}
            tabIndex={0}
          >
            <DrawerTitle className="sr-only">Search</DrawerTitle>
            <DrawerHeader className="p-0">{SearchHeader}</DrawerHeader>

            <div className="flex-1 overflow-y-auto">{MainContent}</div>
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog
      open={isSearchDialogOpen}
      onOpenChange={(newOpen) => {
        // Prevent closing parent dialog during delete confirmation
        if (confirmDeleteId && !newOpen) {
          return;
        }
        setIsSearchDialogOpen(newOpen);
      }}
    >
      <DialogContent
        className="gap-0 p-0 sm:max-w-2xl md:p-0"
        ref={dialogContentRef}
        tabIndex={0}
        onKeyDown={handleKeyDown}
        showDialogClose={false}
      >
        <DialogTitle className="sr-only">Search</DialogTitle>
        {SearchHeader}
        {MainContent}
        {ActionFooter}
      </DialogContent>
    </Dialog>
  );
}
